import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { isUndefined } from 'lodash';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  locationsAccountAtom,
  locationsActivityAtom,
  locationsCountriesAtom,
  locationsProductTypesAtom,
} from 'src/atoms/filters/locations';
import { getProductLastSessionSummaryOptions } from 'src/clients/locations/Device Locations API/@tanstack/react-query.gen';
import { pxToRem } from 'src/common/utils/fonts';
import { toLocaleBrowserSettings } from 'src/i18n/numberLocale';

const StatCardValue = styled(Typography)({
  fontSize: pxToRem(28),
});

const StatCard: FC<{ title: string; children: ReactNode }> = (props) => {
  const { children, title } = props;

  return (
    <Card data-testid={`locations.stats.${title}`} sx={{ width: 'auto', flexGrow: 1 }}>
      <CardContent>
        <Typography
          data-testid={`locations.stats.${title}.title`}
          gutterBottom
          sx={{ color: 'text.secondary', fontSize: pxToRem(18) }}
        >
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
};

const StatCardLoading: FC = () => {
  return (
    <Card sx={{ width: 'auto', flexGrow: 1 }}>
      <CardContent data-testid="locations.stats.loader">
        <Skeleton width="60%" height="1.2em" />
        <Skeleton width="100%" height="1.2em" />
        <Skeleton width="100%" height="1.2em" />
      </CardContent>
    </Card>
  );
};

const StatCardError: FC<{ title: string }> = ({ title }) => {
  const { t } = useTranslation();

  return (
    <StatCard title={title}>
      <Typography
        data-testid={`locations.stats.${title}.error.label`}
        color="textSecondary"
        textAlign="center"
        mt="1em"
      >
        {t('common.somethingWrong')}
      </Typography>
    </StatCard>
  );
};

const StatCardContainer: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Stack
      data-testid="locations.stats"
      width="100%"
      direction="row"
      useFlexGap
      spacing={20}
      justifyContent="stretch"
      flexWrap="wrap"
    >
      {children}
    </Stack>
  );
};

const LocationsStatsSkeleton: FC = () => {
  return (
    <StatCardContainer>
      <StatCardLoading />
      <StatCardLoading />
      <StatCardLoading />
      <StatCardLoading />
    </StatCardContainer>
  );
};

const LocationsStatsFetcher: FC<{ account: string }> = (props) => {
  const { account } = props;
  const { t } = useTranslation();
  const countries = useAtomValue(locationsCountriesAtom);
  const productTypes = useAtomValue(locationsProductTypesAtom);
  const activityDays = useAtomValue(locationsActivityAtom);

  const { data, status } = useQuery({
    ...getProductLastSessionSummaryOptions({
      query: {
        countries,
        accountReferenceId: account,
        productTypes,
        ...(activityDays && { activityDays }),
      },
    }),
    meta: { isLocations: true },
  });

  if (status === 'error') {
    return (
      <StatCardContainer>
        <StatCardError title={t('locations.cards.active24h')} />
        <StatCardError title={t('locations.cards.active30d')} />
        <StatCardError title={t('locations.cards.active90d')} />
        <StatCardError title={t('locations.cards.countries')} />
      </StatCardContainer>
    );
  }

  if (status === 'pending') {
    return <LocationsStatsSkeleton />;
  }

  return (
    <StatCardContainer>
      {isUndefined(data.active24Hours) ? null : (
        <StatCard title={t('locations.cards.active24h')}>
          <StatCardValue
            data-testid={`locations.stats.${t('locations.cards.active24h')}.content`}
            color="green.600"
          >
            {toLocaleBrowserSettings(data.active24Hours ?? 0)}
          </StatCardValue>
        </StatCard>
      )}
      {isUndefined(data.active30Days) ? null : (
        <StatCard title={t('locations.cards.active30d')}>
          <StatCardValue
            data-testid={`locations.stats.${t('locations.cards.active30d')}.content`}
            color="amber.600"
          >
            {toLocaleBrowserSettings(data.active30Days ?? 0)}
          </StatCardValue>
        </StatCard>
      )}
      {isUndefined(data.active90Days) ? null : (
        <StatCard title={t('locations.cards.active90d')}>
          <StatCardValue
            data-testid={`locations.stats.${t('locations.cards.active90d')}.content`}
            color="blue.600"
          >
            {toLocaleBrowserSettings(data.active90Days ?? 0)}
          </StatCardValue>
        </StatCard>
      )}
      {isUndefined(data.countryCount) ? null : (
        <StatCard title={t('locations.cards.countries')}>
          <StatCardValue
            data-testid={`locations.stats.${t('locations.cards.countries')}.content`}
            color="textSecondary"
          >
            {toLocaleBrowserSettings(data.countryCount)}
          </StatCardValue>
        </StatCard>
      )}
    </StatCardContainer>
  );
};

const LocationsStatsSelect: FC = () => {
  const { t } = useTranslation();

  return (
    <StatCardContainer>
      {[
        t('locations.cards.active24h'),
        t('locations.cards.active30d'),
        t('locations.cards.active90d'),
        t('locations.cards.countries'),
      ].map((title) => {
        return (
          <StatCard title={title} key={title}>
            <Typography
              data-testid={`locations.stats.${title}.content`}
              variant="text1"
              color="textSecondary"
            >
              {t('locations.map.selectAnAccount')}
            </Typography>
          </StatCard>
        );
      })}
    </StatCardContainer>
  );
};

export const LocationsStats: FC = () => {
  const account = useAtomValue(locationsAccountAtom);

  if (account) {
    return <LocationsStatsFetcher account={account} />;
  }

  return <LocationsStatsSelect />;
};
